import { HTTPFetchError } from '@racemap/utilities/api-client';
import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrentMapContext } from 'src/lib/customHooks/useCurrentMapContext';
import { EventEditor } from '../../components/EventEditor/EventEditor';
import { useStore } from '../../store/reducers';

const routeErrorStatusTexts: Record<number, string> = {
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
};

export const EventEditorView: FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const currentEvent = useStore((s) => s.events.getter.currentEvent());
  const { loadCurrentEvent, clearCurrentEvent } = useStore((s) => s.events.actions);
  const mapContexts = useStore((s) => s.maps.ctxs);
  const currentMapContext = useCurrentMapContext();
  const { createMapContext, setCurrentMapId } = useStore((s) => s.maps.actions);
  const navigate = useNavigate();
  const location = useLocation();
  const [loadError, setLoadError] = useState<{ eventId: string; status: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    (async () => {
      if (eventId != null && (currentEvent == null || currentEvent.id !== eventId)) {
        try {
          await loadCurrentEvent(eventId);
        } catch (error) {
          // Ignore failures for a load that was superseded by navigation, so we
          // don't set state on an unmounted view or scope the error to a stale
          // eventId. ErrorPage only renders 403/404/500, so map anything else.
          if (cancelled) return;
          const status = error instanceof HTTPFetchError ? error.status : 500;
          setLoadError({ eventId, status: status === 403 || status === 404 ? status : 500 });
        }
      }
    })();

    return function cleanup() {
      cancelled = true;
      clearCurrentEvent();
      setCurrentMapId(null);
    };
  }, [eventId]);

  useEffect(() => {
    if (currentMapContext == null && currentEvent != null) {
      const mapId = `map-editor-${currentEvent.id}`;
      if (!mapContexts.has(mapId)) {
        createMapContext(mapId, currentEvent, {
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
      setCurrentMapId(mapId);
    }
  }, [currentMapContext, currentEvent]);

  useEffect(() => {
    if (currentEvent?.id !== eventId) return;

    if (location.pathname.match(/\/admin\/events\/[a-f0-9]+\/?$/)) {
      if (location.pathname.endsWith('/')) {
        navigate(`${location.pathname}basic`, { replace: true });
      } else {
        navigate(`${location.pathname}/basic`, { replace: true });
      }
    }
  }, [location, currentEvent, eventId]);

  // Throwing here is caught by the route's `errorElement` (ErrorPage), which
  // renders a proper 404/403/500 page. The thrown object matches the shape
  // `isRouteErrorResponse` expects so ErrorPage shows the status result.
  if (loadError != null && loadError.eventId === eventId) {
    throw {
      status: loadError.status,
      statusText: routeErrorStatusTexts[loadError.status] ?? 'Error',
      data: null,
      internal: false,
    };
  }

  if (eventId == null || currentEvent == null) return <></>;

  return <EventEditor />;
};
